import * as yup from "yup";

import {
	BackdropCircularProgress,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	StandardPanel,
	TwoColumnLayout,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, Typography, useTheme } from "@mui/material";
import {
	INITIAL_SNACKBAR_DATA,
} from "../../../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatDate, getAddOnsScreenParentName, scrollPanelFieldsToTop, useNavigateToLogout } from "../../../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AccountSettingsService } from "../../../../services/AddOns/Accounting/AccountSettingsService";

import { BasicTwoColumnLayoutShowOnlyTitleSetupToolbar } from "../../../BasicTwoColumnLayoutShowOnlyTitleSetupToolbar";

import { EmployeeService } from "../../../../services/EmployeeService";
import { IAppState } from "../../../../store/reducers";

import { ImportDataService } from "../../../../services/AddOns/Accounting/ImportDataService";

import { NoFoundPage } from "../../../../pages/Anonymous/NoFoundPage";
import { ImportTimeSheetsInfoSetupFieldsPanel } from "./Panels/ImportTimeSheetsInfoSetupFieldsPanel";
import { ManageEmployeesInfoSetupFieldsPanel } from "./Panels/ManageEmployeesInfoSetupFieldsPanel";
import { LinkEmployeesInfoSetupFieldsPanel } from "./Panels/LinkEmployeesInfoSetupFieldsPanel";
import { setSelectedDrawerOptionsMenu } from "../../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../../store/actions";
import { sortDDLByLabel } from "../../../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";
import { HumanityApiSettingsInfoFieldsPanel } from "./Panels/HumanityApiSettingsInfoFieldsPanel";
import { BambooHrInfoSetupFieldsPanel } from "./Panels/BambooHrInfoSetupFieldsPanel";

// import { ImportDataValidationSchema } from "./validator";

export interface IImportDataSetup { }
// const SINGLE_RECORD_DATA_QUERY_KEY = "single-data-record";
const CONNECT_ZOHO_PERMISSION_DATA_QUERY_KEY = "connect-zoho-permission-record";
const GET_LINK_EMPLOYEES = "get-link-employees-record";

// const useGetConnectionPermission = (
// 	importDataPageName: string,
// 	clientId: string,
// ) =>
// 	useQuery({
// 		queryKey: [CONNECT_ZOHO_PERMISSION_DATA_QUERY_KEY, importDataPageName, clientId],
// 		queryFn: () => {
// 			return AccountSettingsService.getAccountSettingsDDL(importDataPageName, clientId);
// 		},
// 		refetchOnWindowFocus: false,
// });

const useGetLinkedEmployees = (
	clientId: string,
	addOnsType: string,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [GET_LINK_EMPLOYEES, clientId, addOnsType, navigateToLogout],
		queryFn: () => {
			return ImportDataService.getLinkedEmployees(clientId, addOnsType, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});

const useGetLinkedEmployeesDDL = (clientId: string, ImportDataPageName: string | undefined) =>
	useQuery({
		queryKey: ["linked-employees-ddl", clientId, ImportDataPageName],
		queryFn: (data) => {
			return ImportDataService.getLinkedEmployeesDDL(clientId, ImportDataPageName);
		},
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["Employee-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});

export const linkEmployeesTableInitialData = [
	{
		id: "1",
		employeeId: null,
		addOnsEmployeeId: null,
	},
];
export const ImportData: React.FC<IImportDataSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const navigateToLogout = useNavigateToLogout();

	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const [importDataCurrentPanelTag, setImportDataCurrentPanelTag] = useState<string | null>(null);
	const importDataCurrentPanelTagRef = useRef<string | null>(null);

	const [linkEmployeesTableUpdatedData, setLinkEmployeesTableUpdatedData] =
		useState(linkEmployeesTableInitialData);

	const [linkEmployeesFieldsData, setLinkEmployeesFieldsData] =
		useState(linkEmployeesTableInitialData);
	

	const setActivePanelTag = (tag: string) => {
		importDataCurrentPanelTagRef.current = tag; // synchronous, always current
		setImportDataCurrentPanelTag(tag);          // for re-render/UI only
	};

	const { id, ImportDataPageName } = useParams();
	


	// importDataPageName would be like 
	// net_suite
	// toggl
	// agendrix
	// when_i_work
	// swipe_clock
	// zoho_people
	// zoho_books
	// easy_work_force
	// quick_books_time
	// work_flow_max
	// seven_shifts
	// humanity
	// deputy
	// hr_zoho_people
	// hr_wize
	// workable
	// namely
	// bamboo_hr

	const [importDataPageName, setImportDataPageName] = useState<string>("");
	useEffect(() => {
		if (ImportDataPageName && ImportDataPageName.length > 0) {
			setImportDataPageName(ImportDataPageName);
			if (ImportDataPageName !== importDataPageName) {
				formMethods.reset();
			}
		}
	}, [importDataPageName, ImportDataPageName]);

	// const ImportDataValidationSchema = yup.object().shape({
	// 	paymentDate: yup
	// 		.string()
	// 		.nullable()
	// 		.when("$importDataCurrentPanelTag", {
	// 			is: (tag: string | null) => tag === "payroll",
	// 			then: (schema) => schema.required("Payment Date is required"),
	// 			otherwise: (schema) => schema.nullable(),
	// 		}),
	// });

	/////////////// When I Work Import Fields ////////////////////////
	const importTimeSheetFields = ["payrollScheduleId", "workFormat"];
	const linkEmployeesFields = ["employeeId", "addOnsEmployeeId"];

	//////////////////// Humanity Api Settings Import Fields ////////////////////////
	const humanityApiSettingsemailFields = ["login", "password", "key", "employees_mapping"];

	//////////////////// Bamboo Hr And Namely Api Settings Import Fields ////////////////////////
	const BambooHrAndNamelyApiSettingsemailFields = ["company_url", "api_key",];

	const fieldsByTag: Record<string, string[]> = {
		import_timesheets: importTimeSheetFields,
		link_employees: linkEmployeesFields,
		humanity_api_settings: humanityApiSettingsemailFields,
		namely_api_settings: BambooHrAndNamelyApiSettingsemailFields,
		bamboo_hr_api_settings: BambooHrAndNamelyApiSettingsemailFields,
	};

	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isPendingGetLinkedEmployeesData, data: getLinkedEmployeesData } = useGetLinkedEmployees(
		clientId,
		importDataPageName,
		navigateToLogout
	);


	const { isLoading: isLoadingPayrPeriodFrequencyDDL, data: payPeriodFrequnecyDDL } = useGetPayFrequencyDDL(clientId, taxYear);
	const { isLoading: LoadingLinkedEmployeesDDL, data: linkedEmployeesDDL } = useGetLinkedEmployeesDDL(clientId, ImportDataPageName);

	// useEffect(() => {
	// 	if (linkedEmployeesDDL && Array.isArray(linkedEmployeesDDL)) {
	// 		const placeholder = linkedEmployeesDDL.find((item: any) => item.value === null) ?? { label: "--", value: null };

	// 		const generatedRows = linkedEmployeesDDL
	// 			.filter((item: any) => item.value !== null) // exclude "--" from row count
	// 			.map((item: any, index: number) => ({
	// 				id: String(index + 1),
	// 				employeeId: null,
	// 				addOnsEmployeeId: item.value,
	// 				addOnsEmployeeOptions: [placeholder, item], // <-- only "--" + this row's own item
	// 			}));

	// 		setLinkEmployeesTableUpdatedData(generatedRows);
	// 	}
	// }, [linkedEmployeesDDL]);

	useEffect(() => {
		if (linkedEmployeesDDL && Array.isArray(linkedEmployeesDDL)) {
			const placeholder = linkedEmployeesDDL.find((item: any) => item.value === null) ?? { label: "--", value: null };

			const generatedRows = linkedEmployeesDDL
				.filter((item: any) => item.value !== null)
				.map((item: any, index: number) => ({
					id: String(index + 1),
					employeeId: null,
					addOnsEmployeeId: item.value,
					addOnsEmployeeOptions: [placeholder, item],
				}));

			setLinkEmployeesFieldsData(generatedRows);   // <-- changed: feed the panel's display state
			setLinkEmployeesTableUpdatedData(generatedRows); // keep payload state in sync too
		}
	}, [linkedEmployeesDDL]);


	const { isLoading: isLoadingemployeeDDL, data: employeeDDL } = useGetEmployeeDDL(clientId);
	const employeeDDLItems = useMemo(() => {
		if (employeeDDL) {
			if (
				employeeDDL &&
				Array.isArray(employeeDDL)
			) {
				let resources: any[] =
					sortDDLByLabel(employeeDDL).map(
						(resourceItem: any) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "--",
					value: null,
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDL]);

	// useEffect(() => {
	// 	if (accountSettingsDDL && accountSettingsDDL.status === 400 && importDataPageName === 'xero') {
	// 		navigate(`/add-ons/account-settings/xero`)
	// 	} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && importDataPageName === 'zoho_books') {
	// 		navigate(`/add-ons/zoho-books/connect-zoho`)
	// 	} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && importDataPageName === 'net_suite') {
	// 		navigate(`/add-ons/net-suite/setup`)
	// 	} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && importDataPageName === 'quick_books') {
	// 		navigate(`/add-ons/account-settings/quick_books`)
	// 	} else if (accountSettingsDDL && accountSettingsDDL.status === 400 && importDataPageName === 'kashoo') {
	// 		navigate(`/add-ons/account-settings/kashoo`)
	// 	}
	// }, [accountSettingsDDL])

	const { isPending: isSubmitting, mutate: submitImportDataFormFields } =
		useMutation({
			mutationFn: ImportDataService.createImportData,
			onSuccess: (res: IServerError | IResponse | any) => {

				if (res.status === 200) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					scrollPanelFieldsToTop();
					let path = `/add-ons/import-data/${importDataPageName}`;
					navigate(path);
				} else if (res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});

					scrollPanelFieldsToTop();
					let path = `/add-ons/import-data/${importDataPageName}`;
					navigate(path);
				} else {
					setSnackbarData({
						open: true,
						message: res.data.message
							? res.data.message : res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});


	const onSubmit = (values: any) => {
		const selectedPayFrequency = payPeriodFrequnecyDDL?.find((item: any) => item.value === values.payrollScheduleId);
		const payload = {
			clientId: clientId,
			navigateToLogout,
			importDataCurrentPanelTag: values.importDataCurrentPanelTag,
			importDataPageName: importDataPageName,
			...(values.importDataCurrentPanelTag === "toggl" && {
				// coming Soon...
			}),
			...(values.importDataCurrentPanelTag === "agendrix" && {
				// coming Soon...
			}),
			...(importDataPageName === "when_i_work" && values.importDataCurrentPanelTag === "import_timesheets" && {
				payrollScheduleId: values.payrollScheduleId,
				payPeriodStartDate: selectedPayFrequency.startDate,
				payPeriodEndDate: selectedPayFrequency.endDate,
				workFormat: values.workFormat,
				inputHours: 100,
			}),

			...(values.importDataCurrentPanelTag === "swipe_clock" && {
				// coming Soon...
			}),
			...(values.importDataCurrentPanelTag === "zoho_people" && {
				// coming Soon...
			}),
			...(values.importDataCurrentPanelTag === "hr_zoho_people" && {
				// coming Soon...
			}),
			...(values.importDataCurrentPanelTag === "zoho_books" && {
				// coming Soon...
			}),
			...(values.importDataCurrentPanelTag === "easy_work_force" && {
				// coming Soon...
			}),
			...(values.importDataCurrentPanelTag === "quick_books_time" && {
				// coming Soon...
			}),
			...(values.importDataCurrentPanelTag === "work_flow_max" && {
				// coming Soon...
			}),
			...(values.importDataCurrentPanelTag === "seven_shifts" && {
				// coming Soon...
			}),
			...(importDataPageName === "humanity" && values.importDataCurrentPanelTag === "import_timesheets" && {
				payrollScheduleId: values.payrollScheduleId,
				payPeriodStartDate: selectedPayFrequency.startDate,
				payPeriodEndDate: selectedPayFrequency.endDate,
				workFormat: importDataPageName === 'humanity' ? undefined : values.workFormat,
				deductBreaks: importDataPageName === 'humanity' ? values.deductBreaks : undefined,
				inputHours: 100,
			}),
			...(importDataPageName === "humanity" && values.importDataCurrentPanelTag === "humanity_api_settings" && {
				login: values.login,
				password: values.password,
				key: values.key,
				employees_mapping: values.employees_mapping,
			}),
			...(importDataPageName === "namely" && values.importDataCurrentPanelTag === "namely_api_settings" && {
				company_url: values.company_url,
				api_key: values.api_key,

			}),
			...(importDataPageName === "bamboo_hr" && values.importDataCurrentPanelTag === "bamboo_hr_api_settings" && {
				company_url: values.company_url,
				api_key: values.api_key,

			}),
			// ...(importDataPageName === "when_i_work" && values.importDataCurrentPanelTag === "link_employees" && {
			// 	// employeeId: values.employeeId,
			// 	// addOnsEmployeeId: values.addOnsEmployeeId,
			// 	items: linkEmployeesTableUpdatedData,
			// }),
		};

	
		submitImportDataFormFields(payload);
	};

	const ImportButtonClickedHandler = async (tag: string) => {
		formMethods.clearErrors();
		setActivePanelTag(tag); // updates ref synchronously + state for UI

		const fieldsToValidate = fieldsByTag[tag] ?? [];
		const isValid = await formMethods.trigger(fieldsToValidate);

		if (!isValid) return; // errors will show inline, scoped to that panel's fields only

		const values = formMethods.getValues();
		onSubmit({ ...values, importDataCurrentPanelTag: tag }); // tag passed explicitly, not from state
	};

	useEffect(() => {
		if (clientId && getLinkedEmployeesData) {
			Object.keys(getLinkedEmployeesData).forEach((fieldName) => {
				const fieldValue = getLinkedEmployeesData[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				// if (fieldName === "clientId") {
				// 	let path = `/add-ons/zoho-books/import-data${clientId}`
				// 	navigate(path)
				// }
				if (fieldName === 'addOnsEmployeeId' && Array.isArray(fieldValue)) {
					setLinkEmployeesFieldsData(fieldValue);
					setLinkEmployeesTableUpdatedData(fieldValue);
				}
				if (fieldName === 'emplopyeeId' && Array.isArray(fieldValue)) {
					setLinkEmployeesFieldsData(fieldValue);
					setLinkEmployeesTableUpdatedData(fieldValue);
				}
			});
		}
	}, [clientId, getLinkedEmployeesData]);

	const { isPending: isImportingEmployees, mutate: submitImportEmployees } =
		useMutation({
			mutationFn: ImportDataService.createImportEmployees,
			onSuccess: (res: IServerError | IResponse | any) => {
				
				if (res.status === 200 || res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});

					scrollPanelFieldsToTop();
				} else {
					setSnackbarData({
						open: true,
						message: res.data.message
							? res.data.message : res.message
								? res.message
								: "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const onImportEmployeesClickedHandler = async () => {
		const payload = {
			clientId: clientId,
			navigateToLogout,
			importDataPageName: importDataPageName,
		};
		submitImportEmployees(payload);
	};



	const { isPending: isLinkedEmployees, mutate: submitLinkedEmployees } =
		useMutation({
			mutationFn: ImportDataService.createLinkedEmployees,
			onSuccess: (res: IServerError | IResponse | any) => {
				if (res.status === 200 || res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});

					scrollPanelFieldsToTop();
				} else {
					setSnackbarData({
						open: true,
						message: res.data.message
							? res.data.message : res.message
								? res.message
								: "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const onLinkedEmployeesClickedHandler = async () => {
		const linkedList = linkEmployeesTableUpdatedData
			.filter((row: any) => row.employeeId !== null && row.addOnsEmployeeId !== null) // skip rows still at default "--"
			.map((row: any) => ({
				employeeId: row.employeeId,
				addOnsEmployeeId: row.addOnsEmployeeId,
			}));
		const payload = {
			clientId: clientId,
			addOnsType: importDataPageName,
			linkedList,
			navigateToLogout,
		};
		
		submitLinkedEmployees(payload);
	};

	const expandedLabels = ["Add-Ons", `${getAddOnsScreenParentName(importDataPageName)}`];
	const expandedAddOnsOrListItem = "Import Data";
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (parentLabel === `${getAddOnsScreenParentName(importDataPageName)}` && item.label === expandedAddOnsOrListItem) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: isExpanded,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === `${getAddOnsScreenParentName(importDataPageName)}` && parentLabel === `${getAddOnsScreenParentName(importDataPageName)}` && item.label === expandedAddOnsOrListItem && `/add-ons/import-data/${ImportDataPageName}`
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onExportEmployeesClickedHandler = () => {
		alert("Coming Soon...")
	}


	let acceptableImportPages = ["toggl", "agendrix", "when_i_work", "swipe_clock", "zoho_people", "hr_zoho_people", "zoho_books", "easy_work_force", "quick_books_time", "work_flow_max", "seven_shifts", "humanity", "deputy", "hr_wize", "workable", "namely", "bamboo_hr"]

	return (
		<>{ImportDataPageName && acceptableImportPages.includes(ImportDataPageName) ? <Grid>
			<BackdropCircularProgress
				open={isSubmitting || isLoadingPayrPeriodFrequencyDDL || LoadingLinkedEmployeesDDL || isLoadingemployeeDDL || isImportingEmployees || isPendingGetLinkedEmployeesData || isLinkedEmployees}
				testId="import-data"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
							<TwoColumnLayout
								leftColumnContent={
									<>
										{importDataPageName === 'when_i_work' ?
											<StandardPanel
												title="Import Timesheets"
												testId={`import-time-sheet-info`}
												borderHide={true}
											>
												<ImportTimeSheetsInfoSetupFieldsPanel
													payPeriodFrequnecyDDL={payPeriodFrequnecyDDL && sortDDLByLabel(payPeriodFrequnecyDDL)}
													handleImportClick={ImportButtonClickedHandler}
													importDataPageName={importDataPageName}
												/>
											</StandardPanel> : <></>}

										{importDataPageName === 'humanity' ?
											<StandardPanel
												title="Humanity API Settings"
												testId={`humanity-api-settings-info`}
												variant="outlined"
												borderBottom={false}
												borderHide={true}
											>
												<HumanityApiSettingsInfoFieldsPanel
													handleImportClick={ImportButtonClickedHandler}
												/>
											</StandardPanel> : <></>}

										{importDataPageName === 'zoho_people' ? <StandardPanel
											title="Manage Employee(s)"
											testId={`manage-employees-info`}
											borderHide={true}
										>
											<ManageEmployeesInfoSetupFieldsPanel
												onExportEmployeesClickedHandler={onExportEmployeesClickedHandler}
												onImportEmployeesClickedHandler={onImportEmployeesClickedHandler}
												employeeDDL={employeeDDL && sortDDLByLabel(employeeDDL)}
												linkedEmployeesDDL={linkedEmployeesDDL && linkedEmployeesDDL}
												importDataPageName={importDataPageName}
											/>
										</StandardPanel> : <></>
										}

										{importDataPageName === 'bamboo_hr' || importDataPageName === 'namely' ? <StandardPanel
											title={importDataPageName === 'bamboo_hr' ? `BambooHr API Settings` : "Namely API Settings"}
											testId={`bamboo-hr-api-settings-info`}
											borderBottom={importDataPageName === 'bamboo_hr' ? true : false}
											borderHide={true}
										>
											<BambooHrInfoSetupFieldsPanel
												handleImportClick={ImportButtonClickedHandler}
												importDataPageName={importDataPageName}
											/>
										</StandardPanel> : <></>
										}

										{importDataPageName === 'bamboo_hr' ? <StandardPanel
											title="Synchronize Employees"
											testId={`synchronize-employees-info`}
											borderHide={true}
										>
											<Typography sx={{ fontSize: ".875rem", }}>
												No employee data changed after last synchronization/import.
											</Typography>
										</StandardPanel> : <></>
										}
									</>
								}

								rightColumnContent={
									<>
										{importDataPageName === 'when_i_work' || importDataPageName === 'bamboo_hr' || importDataPageName === 'namely' ? <StandardPanel
											title="Manage Employee(s)"
											testId={`manage-employees-info`}
											borderHide={true}>
											<ManageEmployeesInfoSetupFieldsPanel
												onExportEmployeesClickedHandler={onExportEmployeesClickedHandler}
												onImportEmployeesClickedHandler={onImportEmployeesClickedHandler}
												employeeDDL={employeeDDL && sortDDLByLabel(employeeDDL)}
												linkedEmployeesDDL={linkedEmployeesDDL && linkedEmployeesDDL}
												importDataPageName={importDataPageName}
											/>
										</StandardPanel> : <></>}
										{importDataPageName === 'when_i_work' || importDataPageName === 'zoho_people' || importDataPageName === "bamboo_hr" ?
											<StandardPanel
												title="Link Employee(s)"
												testId={`link-employees-info`}
												borderTop={importDataPageName === 'when_i_work' ? true : false}
												borderHide={true}
												variant="outlined"
											>
												<LinkEmployeesInfoSetupFieldsPanel
													employeeDDL={employeeDDLItems ?? []}
													linkedEmployeesDDL={linkedEmployeesDDL && sortDDLByLabel(linkedEmployeesDDL)}
													onLinkedEmployeesClickedHandler={onLinkedEmployeesClickedHandler}
													importDataPageName={importDataPageName}
													linkEmployeesFieldsData={linkEmployeesFieldsData}
													onLinkEmployeesTableChange={(data: any) =>
														setLinkEmployeesTableUpdatedData(data)
													}
												/>
											</StandardPanel> : <></>}

										{importDataPageName === 'humanity' ? <StandardPanel
											title="Import Timesheets"
											testId={`import-time-sheet-info`}
											borderHide={true}
										>
											<ImportTimeSheetsInfoSetupFieldsPanel
												payPeriodFrequnecyDDL={payPeriodFrequnecyDDL && sortDDLByLabel(payPeriodFrequnecyDDL)}
												handleImportClick={ImportButtonClickedHandler}
												importDataPageName={importDataPageName}
											/>
										</StandardPanel> : <></>
										}

										{importDataPageName === 'namely' ? <StandardPanel
											title="Synchronize Employees"
											testId={`synchronize-employees-info`}
											borderHide={true}
										>
											<Typography sx={{ fontSize: ".875rem", }}>
												No employee data changed after last synchronization/import.
											</Typography>
										</StandardPanel> : <></>
										}
									</>
								}
								toolbar={
									<BasicTwoColumnLayoutShowOnlyTitleSetupToolbar
										pageTitle={"Import Data"}
									/>
								}
								testId="form-layout"
							/>
						</Grid>

						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"import-data-snackbar"}
				open={snackbarData.open}
			/>
		</Grid> : <NoFoundPage />}</>
	);
};
